<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Alert, Button, Container, Input, Text } from '@Shelly/shelly-design-system';
	import { is_math_condition_ui, type MathParameter } from 'src/types/Scenes/MathConditionUI';

	import { _deepClone, capFirstChar } from '../../../../../../../../helpers/utilities';
	import { _init_time_based_conditions } from '../../../../../../helpers/init_time_conditions';
	import { WHEN_WIZARD_STEPS_INDEXES } from '../../helpers/when-wizard-helpers';
	import { processExpression } from '../../../../../../helpers/math_based/math_processing_helpers';
	import { _math_condition_message } from 'src/components/Scenes/components/helpers/conditions_utils';
	import MathParamPreview from './MathParams/MathParamPreview.svelte';
	import {
		type MathErrorMessage,
		handleMathError,
	} from 'src/components/Scenes/helpers/math_based/math_processing_errors';
	import MathInstructions from './MathInstructions.svelte';
	import MathParamWizard from './MathParams/MathParamWizard.svelte';

	export let sceneData: any;
	/** Optional adapter used by Flow without changing the Scene wizard contract. */
	export let onChange: ((mathCondition: any, isValid: boolean) => void) | undefined = undefined;

	let showSelectParamModal = false;

	let expressionError: MathErrorMessage;
	let validExpression = true;

	let mathExpression = '';
	let mathParams: MathParameter[] = [];
	let editingParamName = ''; //Currently editing math param

	// $: sceneData, validateInitialProps();
	$: mathParams = sceneData._math_condition?.params || [];
	$: mathExpression = sceneData._math_condition?.expression || '';
	$: showInputMessage = !validExpression && mathExpression.length > 0;
	$: showAddButton = mathParams.length < 10 || _getLastParamName() === 'Z';

	const validateWizardMathCondition = () => {
		sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.MATH_EXPRESSION_STEP;
		sceneData.isValid = validExpression && is_math_condition_ui(sceneData._math_condition);
	};

	const _getLastParamName = (): string => {
		const lastParam = mathParams.findLast((param) => param.name && param.value);
		return lastParam?.name || null;
	};

	const onClickAddParam = () => {
		let lastName = _getLastParamName(); //incr. A, B, C, D, etc.
		editingParamName = lastName ? String.fromCharCode(lastName.charCodeAt(0) + 1) : 'A';

		showSelectParamModal = true;
	};

	const onClickEditParam = (name: string) => {
		showSelectParamModal = true;
		editingParamName = name;
	};

	const onDeleteParam = (name: string) => {
		console.log('Delete param: ', name);
		mathParams = mathParams.filter((param) => param.name !== name);
		handleExpressionChange();
	};

	const onChangeExpressionInput = (cur_expression: string) => {
		mathExpression = cur_expression;
		handleExpressionChange();
	};

	/** Validate user inputs */
	const validateInputs = () => {
		if (!mathParams.length) {
			validExpression = false;
			expressionError = handleMathError('NO_PARAMETERS');
			return false;
		}
		return true;
	};

	/**
	 * Handle change in Math Expression or Params
	 */
	const handleExpressionChange = () => {
		let iotsExpression = {};

		//Input validations
		if (validateInputs()) {
			try {
				//Expression processing
				iotsExpression = processExpression(mathExpression, mathParams);
				validExpression = true;
				expressionError = undefined;
			} catch (error: any) {
				//Error processing expression
				validExpression = false;
				expressionError = handleMathError(error.message, error.cause);
			}
		}

		sceneData._math_condition = {
			math_script: iotsExpression,
			expression: mathExpression,
			params: [...mathParams],
			_gui_type: 'math_based',
		};

		validateWizardMathCondition();
		onChange?.(sceneData._math_condition, sceneData.isValid);
	};

	//Get MathParameter by name
	const getMathParam = (name: string): MathParameter => {
		return mathParams.find((param) => param.name === name);
	};

	/**
	 * Update the math params list
	 * @param name
	 * @param value
	 */
	const _add_edit_math_param = (name: string, value: string) => {
		// Add or edit math param
		if (name) {
			mathParams = mathParams.filter((param) => param.name !== name);
		}
		mathParams = [...mathParams, { name, value }].toSorted((a, b) => a.name.localeCompare(b.name));
	};

	/**
	 * Change Math Parameter value
	 * @param name - Math Parameter name (A,B,C,D, etc.)
	 * @param value - Math Parameter value (e.g. device:123:m1)
	 */
	const onChangeMathParam = (name: string, value: string) => {
		if (import.meta.env.DEV) {
			console.log('[MathStep] Changed Math Parameter[%s]: ', name, value);
		}

		if (!value) {
			return;
		}
		_add_edit_math_param(name, value);
		handleExpressionChange();
	};

	//Editing math based condition; load the math expression and params; process & validate
	if (is_math_condition_ui(sceneData.translated)) {
		mathExpression = sceneData.translated?.expression || '';
		mathParams = sceneData.translated?.params || [];
		handleExpressionChange();
	}
</script>

<!-- Experimental feature info box -->
<Alert
	class="mb-l"
	type="info"
	heading={capFirstChar(
		$format('alert_title_experimental_feature', {
			default: 'Experimental feature',
		}),
	)}
	isHeadingBold
	message={$format('alert_message_experimental_feature', {
		default:
			'This is an experimental feature and may not always work as expected. Please, report any issues.',
	})}
/>

<!-- Modal to choose Device for Property Parameter -->
{#if showSelectParamModal}
	<MathParamWizard
		bind:showModal={showSelectParamModal}
		{editingParamName}
		editingParamValue={getMathParam(editingParamName)?.value}
		onChangeParam={onChangeMathParam}
	/>
{/if}

<Text element="h2" class="mb-s">
	{$format('scenes_title_math_params', {
		default: 'Math Expression Parameters',
	})}
</Text>

{#if !mathParams.length}
	<Container>
		<Text element="div">
			{$format('scenes_math_missing_parameter_label', {
				default:
					'Select device property to use in the math expression. Each parameter will be represented by a letter (A, B, C, etc.) and will correspond to the device value.',
			})}
		</Text>
		<div class="text-center">
			<Button
				variant="secondary"
				size="m"
				class="mt-m text-center"
				icon="fas fa-plus"
				on:click={onClickAddParam}
			>
				{$format('scenes_button_add_math_parameter', {
					default: 'Add parameter',
				})}
			</Button>
		</div>
	</Container>
{:else}
	<div class="mb-s">
		{#each mathParams as param}
			<MathParamPreview
				index={mathParams.indexOf(param)}
				mathParameter={param}
				onClickEdit={onClickEditParam}
				onClickDelete={onDeleteParam}
			/>
		{/each}
	</div>
	{#if showAddButton}
		<Button variant="secondary" size="m" icon="fas fa-plus" on:click={onClickAddParam}>
			{$format('scenes_button_add_math_parameter', {
				default: 'Add parameter',
			})}
		</Button>
	{/if}
{/if}

<Text element="h2" class="mt-l mb-s">
	{capFirstChar(
		$format('scenes_title_math_expression', {
			default: 'Enter math expression',
		}),
	)}
	<MathInstructions />
</Text>
<Input
	aria-labelledby="input-kafnuh0"
	icon="fa fa-square-root-variable"
	size="l"
	value={mathExpression}
	label={$format('scenes_label_math_expression_example', {
		default: 'E.g.: A + B < 20 + (C / 2)',
	})}
	disabled={false}
	error={showInputMessage && expressionError?.type === 'error' ? expressionError?.message : ''}
	info={showInputMessage && expressionError?.type === 'info' ? expressionError?.message : ''}
	on:input={(e) => onChangeExpressionInput(e?.target?.value)}
	id="input-kafnuh0"
/>
